<template>
  <BaseCardSection title="Informace o zákazníkovi" :is-visible="true">
    <div class="row g-3">
      <!-- 1. Výběr zákazníka -->
      <div class="col-md-12">
        <autocomplete-select
            v-model="selectedCustomerId"
            :options="customerOptions"
            label="Vyhledat zákazníka"
            placeholder="Hledat podle jména, emailu nebo IČO..."
        />
      </div>

      <hr class="my-4">

      <!-- 2. Fakturační údaje (Zákazník) -->
      <div class="row align-items-center mb-2">
        <div class="col">
          <h4><span class="badge text-bg-secondary">Fakturační adresa</span></h4>
        </div>
      </div>

      <div class="row g-3" v-if="localCustomer">
        <div class="col-md-6">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Příjmení:</label>
              <input
                  type="text"
                  class="form-control"
                  v-model="localCustomer.lastName"
                  @input="emitCustomer"
                  v-validate="{
                     rules: { required: true },
                     message: { required: 'Příjmení je povinné'},
                     options: { showValidState: false }
                   }"
              />
            </div>
            <div class="col-12">
              <label class="form-label">Jméno:</label>
              <input
                  type="text"
                  class="form-control"
                  v-model="localCustomer.firstName"
                  @input="emitCustomer"/>
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Název společnosti:</label>
              <input type="text" class="form-control" v-model="localCustomer.name" @input="emitCustomer">
            </div>
            <div class="col-md-6">
              <label class="form-label">IČO:</label>
              <div class="input-group">
                <input type="text" class="form-control" v-model="localCustomer.ico" @input="emitCustomer">
                <BaseActionIconButton
                    icon="bi-search"
                    variant="primary"
                    tooltip="Načíst z ARES"
                    :disabled="!localCustomer.ico || localCustomer.ico.length < 8"
                    @click="fetchAresData(localCustomer, emitCustomer)"
                />
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label">DIČ:</label>
              <input type="text" class="form-control" v-model="localCustomer.dic" @input="emitCustomer">
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <label class="form-label">Email:</label>
          <input type="email" class="form-control" v-model="localCustomer.email" @input="emitCustomer">
        </div>
        <div class="col-md-6">
          <label class="form-label">Telefon:</label>
          <input type="text" class="form-control" v-model="localCustomer.phone" @input="emitCustomer">
        </div>

        <div class="col-md-5">
          <label class="form-label">Ulice a č.p.:</label>
          <input type="text" class="form-control" v-model="localCustomer.street" @input="emitCustomer">
        </div>
        <div class="col-md-5">
          <label class="form-label">Město:</label>
          <input type="text" class="form-control" v-model="localCustomer.city" @input="emitCustomer">
        </div>
        <div class="col-md-2">
          <label class="form-label">PSČ:</label>
          <input type="text" class="form-control" v-model="localCustomer.zipcode" @input="emitCustomer">
        </div>
      </div>

      <hr class="my-4">

      <!-- 3. Dodací adresa -->
      <div class="row align-items-center">
        <div class="col">
          <h4>
            <span class="badge text-bg-secondary">Dodací adresa</span>
            <span class="text-sm-center text-muted">&nbsp;&nbsp;{{
                isSameAsBilling ? '(shodná s fakturační)' : ''
              }}</span>
          </h4>
        </div>
        <div class="col-auto">
          <div class="form-check form-switch">
            <input class="form-check-input" type="checkbox" role="switch" id="sameAsBilling"
                   v-model="isSameAsBilling">
            <label class="form-check-label" for="sameAsBilling">Shodná s fakturační</label>
          </div>
        </div>
      </div>

      <div class="row g-3 mt-1" v-if="!isSameAsBilling">
        <div class="col-md-12" v-if="deliveryAddressOptions.length > 1">
          <label class="form-label text-muted">Vybrat z uložených adres:</label>
          <select class="form-select form-select" v-model="selectedDeliveryAddressId">
            <option v-for="opt in deliveryAddressOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
          <hr class="my-3">
        </div>

        <template v-if="localDeliveryAddress">
          <div class="col-md-6">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label">Příjmení (dodání):</label>
                <input
                    type="text"
                    class="form-control"
                    v-model="localDeliveryAddress.lastName"
                    @input="emitDelivery"
                    v-validate="{
                     rules: { required: true },
                     message: { required: 'Příjmení je povinné'},
                     options: { showValidState: false }
                   }">
              </div>
              <div class="col-12">
                <label class="form-label">Jméno (dodání):</label>
                <input
                    type="text"
                    class="form-control"
                    v-model="localDeliveryAddress.firstName"
                    @input="emitDelivery" />
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label">Název společnosti (dodání):</label>
                <input type="text" class="form-control" v-model="localDeliveryAddress.name" @input="emitDelivery">
              </div>
              <div class="col-md-6">
                <label class="form-label">IČO (dodání):</label>
                <div class="input-group">
                  <input type="text" class="form-control" v-model="localDeliveryAddress.ico" @input="emitDelivery">
                  <BaseActionIconButton
                      icon="bi-search"
                      variant="primary"
                      tooltip="Načíst z ARES"
                      :disabled="!localDeliveryAddress.ico || localDeliveryAddress.ico.length < 8"
                      @click="fetchAresData(localDeliveryAddress, emitDelivery)"
                  />
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">DIČ (dodání):</label>
                <input type="text" class="form-control" v-model="localDeliveryAddress.dic" @input="emitDelivery">
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <label class="form-label">Email (dodání):</label>
            <input type="email" class="form-control" v-model="localDeliveryAddress.email" @input="emitDelivery">
          </div>
          <div class="col-md-6">
            <label class="form-label">Telefon (dodání):</label>
            <input
                type="text"
                class="form-control"
                v-model="localDeliveryAddress.phone"
                @input="emitDelivery"
                v-validate="{
                     rules: { required: true },
                     message: { required: 'Telefon je povinný'},
                     options: { showValidState: false }
                   }">
          </div>

          <div class="col-md-5">
            <label class="form-label">Ulice a č.p. (dodání):</label>
            <input
                type="text"
                class="form-control"
                v-model="localDeliveryAddress.street"
                @input="emitDelivery"
                v-validate="{
                     rules: { required: true },
                     message: { required: 'Ulice je povinná'},
                     options: { showValidState: false }
                   }">
          </div>
          <div class="col-md-5">
            <label class="form-label">Město (dodání):</label>
            <input
                type="text"
                class="form-control"
                v-model="localDeliveryAddress.city"
                @input="emitDelivery"
                v-validate="{
                     rules: { required: true },
                     message: { required: 'Město je povinné'},
                     options: { showValidState: false }
                   }">
          </div>
          <div class="col-md-2">
            <label class="form-label">PSČ (dodání):</label>
            <input
                type="text"
                class="form-control"
                v-model="localDeliveryAddress.zipcode"
                @input="emitDelivery"
                v-validate="{
                     rules: { required: true },
                     message: { required: 'Město je povinné'},
                     options: { showValidState: false }
                   }">
          </div>
        </template>
      </div>
    </div>
  </BaseCardSection>
</template>

<script setup>
import {reactive, ref, onMounted, watch, defineProps, defineEmits} from 'vue';
import BaseCardSection from "@/components/base/BaseCardSection.vue";
import AutocompleteSelect from "@/components/base/AutocompleteSelect.vue";
import BaseActionIconButton from "@/components/base/BaseActionIconButton.vue";
import api from "@/api/api";
import DeliveryAddress from "@/models/DeliveryAddress";
import Customer from "@/models/Customer";
import {getFullCustomerName} from "@/common/functions";

const props = defineProps({
  customer: {type: Object, required: true},
  deliveryAddress: {type: Object, required: false, default: null}
});

const emit = defineEmits(['update:customer', 'update:deliveryAddress']);

// Lokální reaktivní stavy
const localCustomer = reactive({...props.customer});
const localDeliveryAddress = ref(props.deliveryAddress ? reactive({...props.deliveryAddress}) : null);
const isSameAsBilling = ref(!props.deliveryAddress || (!props.deliveryAddress.id && !props.deliveryAddress.street));

const selectedCustomerId = ref(localCustomer.id || null);
const customerOptions = ref([]);
const rawCustomers = ref([]);
const deliveryAddressOptions = ref([]);
const selectedDeliveryAddressId = ref(null);
const rawDeliveryAddresses = ref([]);

// Pomocné funkce pro emity
const emitCustomer = () => emit('update:customer', {...localCustomer});
const emitDelivery = () => emit('update:deliveryAddress', isSameAsBilling.value ? null : {...localDeliveryAddress.value});

/**
 * Zobecněná funkce pro načtení dat z ARES
 * @param {Object} targetObj - Objekt, do kterého se mají data uložit (localCustomer nebo localDeliveryAddress)
 * @param {Function} callback - Funkce pro emitování změn po načtení
 */
const fetchAresData = async (targetObj, callback) => {
  if (!targetObj || !targetObj.ico) return;

  try {
    const data = await api.aresSubjectByIco(targetObj.ico);
    if (data) {
      targetObj.name = data.name || targetObj.name;
      targetObj.dic = data.dic || targetObj.dic;

      if (data.address) {
        targetObj.city = data.address.town || targetObj.city;
        targetObj.zipcode = data.address.zipcode ? String(data.address.zipcode) : targetObj.zipcode;

        // Sestavení ulice (ulice + čísla)
        let street = data.address.street || data.address.town;
        const hn = data.address.street_hn;
        const op = data.address.street_op;

        if (hn && op) street += ` ${hn}/${op}`;
        else if (hn) street += ` ${hn}`;

        targetObj.street = street;
      }
      if (callback) callback();
    } else {
      alert("Subjekt s tímto IČO nebyl v ARES nalezen.");
    }
  } catch (error) {
    console.error(error);
    alert("Chyba při komunikaci s ARES API.");
  }
};

// Watchery pro synchronizaci zvenčí
watch(() => props.customer, (newVal) => {
  if (JSON.stringify(newVal) !== JSON.stringify(localCustomer)) {
    Object.assign(localCustomer, newVal);
    selectedCustomerId.value = newVal.id;
  }
}, {deep: true});

watch(() => props.deliveryAddress, (newVal) => {
  if (JSON.stringify(newVal) !== JSON.stringify(localDeliveryAddress.value)) {
    if (newVal) {
      localDeliveryAddress.value = reactive({...newVal});
      isSameAsBilling.value = false;
    } else {
      localDeliveryAddress.value = null;
      isSameAsBilling.value = true;
    }
  }
}, {deep: true});

// Logika přepínače Shodná s fakturační
watch(isSameAsBilling, async (val) => {
  if (val) {
    localDeliveryAddress.value = null;
    selectedDeliveryAddressId.value = null;
  } else {
    if (!localDeliveryAddress.value) {
      localDeliveryAddress.value = reactive(new DeliveryAddress({}));
    }
    if (localCustomer.id) {
      await loadDeliveryAddresses(localCustomer.id);
    }
  }
  emitDelivery();
});

const loadCustomers = async () => {
  const data = await api.customerListFiltered(null);
  rawCustomers.value = data;
  const options = [{value: -1, label: 'Nový zákazník...'}];
  data.forEach(c => {
    options.push({value: c.id, label: getFullCustomerName(c.name, c.first_name, c.last_name, c.email)});
  });
  customerOptions.value = options.sort((a, b) => a.label.localeCompare(b.label, 'cs'));
};

const loadDeliveryAddresses = async (customerId) => {
  const addresses = await api.getCustomerAddresses(customerId);
  rawDeliveryAddresses.value = addresses;
  const options = [{value: 'new', label: '+ Vytvořit novou adresu...'}];
  addresses.forEach(a => {
    options.push({value: a.id, label: getFullCustomerName(a.name, a.first_name, a.last_name, a.email)});
  });
  deliveryAddressOptions.value = options.sort((a, b) => a.label.localeCompare(b.label, 'cs'));
  if (addresses.length === 0) selectedDeliveryAddressId.value = 'new';
};

watch(selectedDeliveryAddressId, (newAddrId) => {
  if (newAddrId === 'new' || !newAddrId) {
    localDeliveryAddress.value = reactive(new DeliveryAddress({}));
  } else {
    const addr = rawDeliveryAddresses.value.find(a => a.id === newAddrId);
    if (addr) localDeliveryAddress.value = reactive(new DeliveryAddress(addr));
  }
  emitDelivery();
});

watch(selectedCustomerId, async (newId) => {
  const id = Number(newId);
  isSameAsBilling.value = true;

  if (!id || id === -1) {
    Object.assign(localCustomer, new Customer(null));
  } else {
    const customer = rawCustomers.value.find(c => Number(c.id) === id);
    if (customer) {
      Object.assign(localCustomer, {
        id: customer.id,
        name: customer.name || '',
        firstName: customer.first_name || '',
        lastName: customer.last_name || '',
        ico: customer.ico || '',
        dic: customer.dic || '',
        email: customer.email || '',
        phone: customer.phone || '',
        street: customer.street || '',
        city: customer.city || '',
        zipcode: customer.zipcode || ''
      });
    }
  }
  emitCustomer();
  emitDelivery();
});

onMounted(() => {
  loadCustomers();
  if (props.deliveryAddress && (props.deliveryAddress.id || props.deliveryAddress.street)) {
    isSameAsBilling.value = false;
    if (localCustomer.id) {
      loadDeliveryAddresses(localCustomer.id).then(() => {
        selectedDeliveryAddressId.value = props.deliveryAddress.id || 'new';
      });
    }
  }
});
</script>